import { Suspense, StrictMode } from 'react';
import ReactDOM from 'react-dom';
import * as serviceWorker from './serviceWorker';
import { Route, Routes, BrowserRouter as Router } from 'react-router-dom';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import App from './App';
import Login from 'containers/Auth/Login';
import SignUp from 'containers/Auth/SignUp';
import { MargaretProvider } from '@koob/margaret';
import { theme } from 'ui/theme';
import AuthProvider from './providers/AuthProvider';
import { KoobMaintenanceGate, KoobSnackProvider } from '@koob/ui';
import { QueryClient, QueryClientProvider } from 'react-query';
import 'sanitize.css';
import 'sanitize.css/typography.css';
import 'sanitize.css/forms.css';
import 'flag-icon-css/css/flag-icon.css';

import { initGTM } from '@koob/google-tag-manager';
import { initUmami } from '@koob/umami';
import { configureRealtime } from '@koob/realtime';
import { API_NODE_BASE } from './constants';
import { syncDocumentLanguage } from '@koob/utils';
import './index.css';
import Impersonate from './containers/Auth/Impersonate';
import AuthRedirect from './containers/Auth/AuthRedirect';
import PublicTravelDiary from './containers/PublicTravelDiary';
import { Navigate } from 'react-router';

initGTM(process.env.REACT_APP_TO_GTM_ID);
initUmami({
  scriptUrl: process.env.REACT_APP_UMAMI_SCRIPT_URL,
  websiteId: process.env.REACT_APP_TO_UMAMI_WEBSITE_ID,
});

// EventSource cannot go through the axios instances (no custom headers), so the
// realtime client gets the base URL and the token reader directly. Impersonation
// wins, exactly like the axios request interceptor.
configureRealtime({
  baseUrl: API_NODE_BASE,
  getAccessToken: () =>
    localStorage.getItem('impersonationToken') ||
    localStorage.getItem('accessToken'),
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// The app is English-only for now (no i18n rollout yet). Kept as a named
// constant so a future i18n strategy can swap it for a real resolver
// (e.g. detectInitialLanguage()) in a single place.
const initialLanguage = 'en';

i18n.use(initReactI18next).init({
  fallbackLng: 'en',
  lng: initialLanguage,
  defaultNS: 'shared',
  // React already escapes everything it renders, so i18next's own escaping is
  // pure damage here: it turns any interpolated `/`, `'`, `&`, `<`, `>` or `"`
  // into an entity that reaches the DOM as literal text — a date interpolated
  // into a label came out as `09&#x2F;09&#x2F;2026`. The only sites that build
  // raw HTML (dangerouslySetInnerHTML) inject API content, never `t()` output.
  interpolation: { escapeValue: false },
  resources: {
    fr: require('./locales/fr.json'),
    en: require('./locales/en.json'),
  },
});

// Keep <html lang> accurate from the first paint (accessibility + future i18n).
// Browser translation itself is fully disabled at the document level via
// translate="no" + the notranslate meta in index.html.
syncDocumentLanguage(initialLanguage);

ReactDOM.render(
  <StrictMode>
    <Suspense fallback={<div />}>
      <KoobMaintenanceGate apiBase={API_NODE_BASE}>
        <QueryClientProvider client={queryClient}>
          <MargaretProvider theme={theme}>
            <KoobSnackProvider>
              <AuthProvider>
                <Router>
                  <Routes>
                    <Route path="login" element={<Login />} />
                    <Route path="sign-up" element={<SignUp />} />
                    <Route
                      path="sign-in"
                      element={<Navigate to="/sign-up" />}
                    />
                    <Route path="auth-redirect" element={<AuthRedirect />} />
                    <Route path="impersonate" element={<Impersonate />} />
                    <Route
                      path="travel-diary/:shareToken"
                      element={<PublicTravelDiary />}
                    />
                    <Route path="/*" element={<App />} />
                  </Routes>
                </Router>
              </AuthProvider>
            </KoobSnackProvider>
          </MargaretProvider>
        </QueryClientProvider>
      </KoobMaintenanceGate>
    </Suspense>
  </StrictMode>,
  document.getElementById('root'),
);

serviceWorker.unregister();
